import type { StructuredSummary } from "@/hooks/dialerEventTypes";
import type { TranscriptionState } from "@/lib/transcriptionState";

export type { StructuredSummary };

export type CallSummaryView = {
  /**
   * The backing CallSummary row id, or NULL when this view describes a call that has
   * no CallSummary row yet — every pre-summary transcription state (in_progress,
   * too_short, low_yield, not_enabled) is by definition such a call.
   *
   * 🚨 NULL is what makes Save-to-Notes structurally impossible for those views, rather
   * than merely unreachable: dismiss() needs a real row, and autoFileSummaryNote is the
   * single writer. Do not substitute a synthetic id to satisfy the type — the card's
   * React key comes from the thread event id, which is independently stable.
   */
  summaryId: string | null;
  status: string;
  skipReason?: string | null;
  /**
   * Server-derived transcription state for the call this summary belongs to
   * (call-events payload). When present it takes precedence over `skipReason`
   * in SummaryState: it explains WHY there is no summary at all, which
   * `skipReason` alone cannot for a call with no CallSummary row.
   */
  transcriptionState?: TranscriptionState | null;
  /**
   * Languages detected in this call's audio, dominant first (call-events
   * payload, PR-B of #1259). Rendered via `describeCallLanguage` above the
   * summary/state content. `[]`/absent renders nothing.
   */
  detectedLanguages?: string[];
  paragraph: string | null;
  structured: StructuredSummary | null;
  noteText: string | null;
  transcriptTruncated?: boolean;
  outcome?: string | null;
  /** Call.callType. A manual ("human_*") call's CALLBACK_REQUESTED is the rep's own
   *  "Follow-up" disposition and is labeled that way. Absent → the generic label. */
  callType?: string | null;
  durationSeconds?: number | null;
  /** The backing Call row id (null for inbound calls, which have no editable
   *  Call.outcome). Lets the AI-suggested-outcome affordance target the
   *  outcome-edit mutation without a second lookup. */
  callId?: string | null;
  contactName?: string;
  phone?: string;
  /**
   * The property this summary belongs to. When null the summary is an
   * orphan (no backing Property row) and the Save-to-Notes action is
   * hidden — Copy-only mode.
   */
  propertyId?: string | null;
  /**
   * ISO timestamp of when a rep pressed Save to Notes. Account-wide and
   * server-persisted, so it survives a reload.
   *
   * Non-null means a note WAS filed. It does not mean one still exists — see noteLive.
   */
  dismissedAt?: string | null;
  /**
   * Whether the filed PropertyNote still exists (not soft-deleted). The card collapses
   * to a receipt only when `dismissedAt != null && noteLive === true`. If the rep deletes
   * the note, this goes false and the full card returns with Save available — which
   * re-files through the P2002 un-delete path, because dismissSummary deliberately does
   * NOT short-circuit on an already-dismissed row (dismissSummary.mjs). Nothing is
   * stranded. Covered on this side by the "note was deleted" card test.
   */
  noteLive?: boolean;
};

/**
 * The popup's own sonner toaster (providers.tsx). 🚨 A toaster with an id renders ONLY toasts
 * carrying that toasterId, so removing or renaming the <Toaster> silently hides every popup.
 * One constant, used by both sides, so the two can never drift.
 */
export const CALL_SUMMARY_TOASTER_ID = "call-summary";

/** The popup's toast id. useDismissSummary closes the popup by it after a save from ANY surface. */
export const callSummaryToastId = (summaryId: string) => `call-summary:${summaryId}`;
